import { supabase } from '@/api/supabaseClient';

// Base44-shaped entity access on top of PostgREST.
//
// The call conventions are deliberately those of the old SDK — positional
// (sort, limit, skip) arguments and '-field' descending sort — so that the
// ~45 existing call sites in src/pages and src/components did not all have to
// change at once. New code can use `supabase.from(...)` directly; this layer is
// meant to be thinned out over time, not built on.

const PLATFORM_TO_COLUMN = {
  // Base44 stamped every row with created_date; Postgres convention is created_at.
  created_date: 'created_at',
  updated_date: 'updated_at',
};

// '-created_date' -> { column: 'created_at', ascending: false }
function parseSort(sort) {
  if (!sort) return null;
  const descending = sort.startsWith('-');
  const raw = descending ? sort.slice(1) : sort;
  if (!raw) return null;
  return { column: PLATFORM_TO_COLUMN[raw] ?? raw, ascending: !descending };
}

function unwrap({ data, error }) {
  if (error) throw error;
  return data;
}

// Base44 rows exposed created_date/updated_date. Keep serving those names so
// components that format them keep working.
function withLegacyDates(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    created_date: row.created_at ?? null,
    updated_date: row.updated_at ?? null,
  };
}

function decorate(data) {
  return Array.isArray(data) ? data.map(withLegacyDates) : withLegacyDates(data);
}

function applyFilters(query, where = {}) {
  let q = query;
  for (const [key, value] of Object.entries(where)) {
    const column = PLATFORM_TO_COLUMN[key] ?? key;
    if (value === null) q = q.is(column, null);
    else if (Array.isArray(value)) q = q.in(column, value);
    else q = q.eq(column, value);
  }
  return q;
}

function applySortAndRange(query, sort, limit, skip) {
  let q = query;
  const order = parseSort(sort);
  if (order) q = q.order(order.column, { ascending: order.ascending });
  if (typeof limit === 'number' && limit > 0) {
    const from = typeof skip === 'number' && skip > 0 ? skip : 0;
    q = q.range(from, from + limit - 1);
  } else if (typeof skip === 'number' && skip > 0) {
    q = q.range(skip, skip + 999);
  }
  return q;
}

// `table` is where writes go; `readTable` is where reads come from when the two
// differ (profiles is written directly but read through its public view).
function makeEntity(table, { readTable = table, realtime = false } = {}) {
  const entity = {
    async list(sort, limit, skip) {
      const q = applySortAndRange(supabase.from(readTable).select('*'), sort, limit, skip);
      return decorate(unwrap(await q));
    },

    async filter(where, sort, limit, skip) {
      const q = applySortAndRange(
        applyFilters(supabase.from(readTable).select('*'), where),
        sort, limit, skip,
      );
      return decorate(unwrap(await q));
    },

    async get(id) {
      const { data, error } = await supabase.from(readTable).select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return decorate(data);
    },

    // Cheap count — Base44 had no count API, so callers used to fetch every row
    // just to call .length on it.
    async count(where = {}) {
      const q = applyFilters(supabase.from(readTable).select('id', { count: 'exact', head: true }), where);
      const { count, error } = await q;
      if (error) throw error;
      return count ?? 0;
    },

    async create(values) {
      const { data, error } = await supabase.from(table).insert(values).select().single();
      if (error) throw error;
      return decorate(data);
    },

    async update(id, values) {
      const { data, error } = await supabase.from(table).update(values).eq('id', id).select().single();
      if (error) throw error;
      return decorate(data);
    },

    async delete(id) {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      return { id };
    },
  };

  if (realtime) {
    // Base44 handed the callback { type: 'create' | 'update' | 'delete', data }.
    // Postgres changes carry new/old rows instead, so translate. REPLICA
    // IDENTITY FULL on these tables means a delete still carries every column,
    // which the Base44 payload did not.
    entity.subscribe = (callback) => {
      const channel = supabase
        .channel(`public:${table}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table },
          (payload) => {
            const type = { INSERT: 'create', UPDATE: 'update', DELETE: 'delete' }[payload.eventType];
            const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
            callback({ type, data: withLegacyDates(row), old: withLegacyDates(payload.old) });
          },
        )
        .subscribe();

      return () => supabase.removeChannel(channel);
    };
  }

  return entity;
}

export const Coffee = makeEntity('coffees');
export const Review = makeEntity('reviews', { realtime: true });
export const ReviewLike = makeEntity('review_likes', { realtime: true });
export const CoffeeChangeRequest = makeEntity('coffee_change_requests');

// Reads go to the public projection (no email, no legacy id); the app only ever
// writes its *own* profile, which auth.updateMe handles.
export const User = makeEntity('profiles', { readTable: 'public_profiles' });

export const entities = { Coffee, Review, ReviewLike, CoffeeChangeRequest, User };
